import mixpanel from "mixpanel-browser";
import { EventName, EventProps } from "./events";

export interface PeopleProperties {
  internal_user?: boolean;
  primary_account_type?: "admin" | "investor" | "mixed" | "nominee";
}

export interface AnalyticsClient {
  init(): void;
  identify(distinctId: string): void;
  reset(): void;
  register(superProps: Record<string, unknown>): void;
  setProfile(props: PeopleProperties): void;
  track<E extends EventName>(event: E, props?: EventProps<E>): void;
  optIn(): void;
  optOut(): void;
}

let initialised = false;

/** Never throws — a failure to record analytics must never break a user workflow.
 * Errors are logged (not surfaced) so the failure is still diagnosable in dev tools / client-side
 * error reporting without ever propagating to the surrounding React tree. */
function safely(fn: () => void): void {
  try {
    fn();
  } catch (error) {
    console.error("Mixpanel analytics call failed:", error);
  }
}

function isEnabled(): boolean {
  return Boolean(window.config?.MIXPANEL_ENABLED && window.config?.MIXPANEL_TOKEN);
}

export const analyticsClient: AnalyticsClient = {
  init() {
    if (initialised || !isEnabled()) {
      return;
    }
    safely(() => {
      mixpanel.init(window.config.MIXPANEL_TOKEN, {
        api_host: window.config.MIXPANEL_API_HOST,
        persistence: "localStorage",
        track_pageview: false,
        ignore_dnt: false,
      });
      initialised = true;
    });
  },

  identify(distinctId: string) {
    if (!initialised || !isEnabled()) return;
    safely(() => mixpanel.identify(distinctId));
  },

  reset() {
    if (!initialised || !isEnabled()) return;
    safely(() => mixpanel.reset());
  },

  register(superProps: Record<string, unknown>) {
    if (!initialised || !isEnabled()) return;
    safely(() => mixpanel.register(superProps));
  },

  setProfile(props: PeopleProperties) {
    if (!initialised || !isEnabled()) return;
    safely(() => mixpanel.people.set(props));
  },

  track<E extends EventName>(event: E, props?: EventProps<E>) {
    if (!initialised || !isEnabled()) return;
    safely(() => mixpanel.track(event, props as Record<string, unknown> | undefined));
  },

  optIn() {
    if (!initialised || !isEnabled()) return;
    safely(() => mixpanel.opt_in_tracking());
  },

  optOut() {
    if (!initialised || !isEnabled()) return;
    safely(() => mixpanel.opt_out_tracking());
  },
};

/** Test-only: resets the module-level init guard between test files. */
export function __resetForTests(): void {
  initialised = false;
}
